import React from 'react';
import { MMMConfigFormState, FREQUENCY_OPTIONS, REGRESSION_ENGINE_OPTIONS, KPI_TYPE_OPTIONS, KPIType, DataFrequency, RegressionEngine, TrainTestSplit } from '../../types/mmmConfig';
import { RadioGroup } from '../FormFields';

interface GlobalSettingsSectionProps {
  config: MMMConfigFormState;
  dataColumns: string[];
  onChange: (config: Partial<MMMConfigFormState>) => void;
  errors: Record<string, string | undefined>;
  isAdvancedMode?: boolean;
}

export const GlobalSettingsSection: React.FC<GlobalSettingsSectionProps> = ({
  config,
  dataColumns,
  onChange,
  errors,
  isAdvancedMode = false,
}) => {
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-5">Global Settings</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <div>
          <label htmlFor="dep_var" className="block text-sm font-medium text-gray-700 mb-1.5">
            Dependent Variable <span className="text-red-500">*</span>
          </label>
          <select
            id="dep_var"
            value={config.dep_var}
            onChange={(e) => onChange({ dep_var: e.target.value })}
            className={`w-full px-3 py-2 text-sm border rounded-md ${errors.dep_var ? 'border-red-400' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900`}
          >
            <option value="">Select target variable...</option>
            {dataColumns.map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
          {errors.dep_var && <p className="text-red-500 text-xs mt-1">{errors.dep_var}</p>}
          <p className="text-gray-500 text-xs mt-1">The variable to model (revenue, sales, etc.)</p>
        </div>

        <div>
          <label htmlFor="date_var" className="block text-sm font-medium text-gray-700 mb-1.5">
            Date Column <span className="text-red-500">*</span>
          </label>
          <select
            id="date_var"
            value={config.date_var}
            onChange={(e) => onChange({ date_var: e.target.value })}
            className={`w-full px-3 py-2 text-sm border rounded-md ${errors.date_var ? 'border-red-400' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900`}
          >
            <option value="">Select date column...</option>
            {dataColumns.map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
          {errors.date_var && <p className="text-red-500 text-xs mt-1">{errors.date_var}</p>}
          <p className="text-gray-500 text-xs mt-1">Must be in YYYY-MM-DD format</p>
        </div>
      </div>

      {isAdvancedMode && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label htmlFor="kpi_type" className="block text-sm font-medium text-gray-700 mb-1.5">
                KPI Type
              </label>
              <select
                id="kpi_type"
                value={config.kpi_type || 'null'}
                onChange={(e) => onChange({ kpi_type: (e.target.value === 'null' ? null : e.target.value) as KPIType })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900"
              >
                {KPI_TYPE_OPTIONS.map((opt) => <option key={opt.value || 'null'} value={opt.value || 'null'}>{opt.label}</option>)}
              </select>
              <p className="text-gray-500 text-xs mt-1">Auto-detect is recommended</p>
            </div>

            <div>
              <label htmlFor="dep_var_label" className="block text-sm font-medium text-gray-700 mb-1.5">
                Display Label
              </label>
              <input
                id="dep_var_label"
                type="text"
                value={config.dep_var_label || ''}
                onChange={(e) => onChange({ dep_var_label: e.target.value || null })}
                placeholder="e.g., Revenue, Conversions"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900"
              />
              <p className="text-gray-500 text-xs mt-1">For reports and charts</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <RadioGroup
              name="frequency"
              label="Data Frequency"
              value={config.frequency || 'W'}
              onChange={(val) => onChange({ frequency: val as DataFrequency })}
              options={FREQUENCY_OPTIONS}
            />

            <RadioGroup
              name="regression_engine"
              label="Regression Engine"
              value={config.regression_engine || 'ridge'}
              onChange={(val) => onChange({ regression_engine: val as RegressionEngine })}
              options={REGRESSION_ENGINE_OPTIONS}
            />
          </div>

          <div className="border-t border-gray-200 pt-5 mt-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Data Splitting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="train_ratio" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Train Ratio
                </label>
                <input
                  id="train_ratio"
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="1.0"
                  value={config.train_test_split?.train_ratio ?? 0.8}
                  onChange={(e) => onChange({ train_test_split: { ...config.train_test_split, train_ratio: parseFloat(e.target.value) } as TrainTestSplit })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900"
                />
                <p className="text-gray-500 text-xs mt-1">Proportion for training (0.8 = 80%)</p>
              </div>

              <div>
                <label htmlFor="test_ratio" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Test Ratio (Optional)
                </label>
                <input
                  id="test_ratio"
                  type="number"
                  step="0.01"
                  min="0.0"
                  max="0.5"
                  value={config.train_test_split?.test_ratio ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : parseFloat(e.target.value);
                    onChange({ train_test_split: { ...config.train_test_split, test_ratio: val } as TrainTestSplit });
                  }}
                  placeholder="e.g., 0.1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900"
                />
                <p className="text-gray-500 text-xs mt-1">Hold-out for out-of-sample testing</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
